import type { Pos } from "@ext/git/core/Diff/logic/model/DiffLine";
import { Fragment, Node } from "prosemirror-model";
import type { EditorState, Transaction } from "prosemirror-state";

const TABLE_ROLES = new Set(["row", "cell", "header_cell"]);

const keepsTableShape = (current: Node, old: Node) => {
	const role = current.type.spec.tableRole;
	if (!TABLE_ROLES.has(role)) return true;
	if (role === "row") return current.childCount === old.childCount;
	return current.attrs.colspan === old.attrs.colspan && current.attrs.rowspan === old.attrs.rowspan;
};

const inSchema = (state: EditorState, node: Node) => Node.fromJSON(state.schema, node.toJSON());

const nodeAtRange = (state: EditorState, range: Pos): Node | null => {
	if (range.from < 0 || range.to + 1 > state.doc.content.size) return null;
	const current = state.doc.nodeAt(range.from);
	return current && range.from + current.nodeSize === range.to + 1 ? current : null;
};

export const restoreModified = (state: EditorState, range: Pos, old: Node): Transaction | null => {
	const current = nodeAtRange(state, range);
	if (!current) return null;
	if (current.type.name !== old.type.name || !keepsTableShape(current, old)) return null;

	const resolved = state.doc.resolve(range.from);
	const index = resolved.index();
	const restored = inSchema(state, old);
	if (!resolved.parent.canReplaceWith(index, index + 1, restored.type)) return null;
	return state.tr.replaceWith(range.from, range.to + 1, restored);
};

export const restoreDeleted = (state: EditorState, at: number, nodes: Node[]): Transaction | null => {
	if (!nodes.length || at < 0 || at > state.doc.content.size) return null;
	try {
		const content = Fragment.from(nodes.map((node) => inSchema(state, node)));
		const tr = state.tr.replaceWith(at, at, content);
		return tr.doc.content.size - state.doc.content.size >= content.size ? tr : null;
	} catch {
		return null;
	}
};

export const restoreInserted = (state: EditorState, range: Pos): Transaction | null => {
	if (!nodeAtRange(state, range)) return null;
	const tr = state.tr.delete(range.from, range.to + 1);
	return tr.doc.content.size < state.doc.content.size ? tr : null;
};
